import { z } from "zod";
import { RunStatusSchema } from "./generation-run.js";
/**
 * Step 5 — two-origin build authorization / generation-input binding origin.
 * CONFIRMED_CANDIDATE: confirm → fresh start-build path.
 * ACTIVE_HEAD_REBUILD: /v1/runs compatibility rebuild against active head.
 */
export const BuildOriginSchema = z.enum(["CONFIRMED_CANDIDATE", "ACTIVE_HEAD_REBUILD"]);
/**
 * Statuses that hold the project generation lock (one nonterminal attempt).
 * Matches the partial unique index for in-flight generation.
 */
export const GENERATION_LOCK_ACTIVE_STATUSES = [
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
];
/**
 * Step 5 supersession set for new-run create (runContractVersion >= 2).
 *
 * Only unprotected / non-shippable statuses may be superseded at create time.
 * Protected statuses (QA_PASSED, QA_PASSED_WITH_WARNINGS, EXPORT_READY, EXPORTING,
 * EXPORTED, SUPERSEDED, RETRIED) are intentionally excluded — prior passing /
 * approved / export evidence remains authoritative until a newer run independently
 * qualifies.
 *
 * Legacy v1 set remains `RUN_STATUSES_SUPERSEDED_ON_NEW_RUN_CREATE` in generation-run.ts.
 */
export const RUN_STATUSES_SUPERSEDED_ON_NEW_RUN_CREATE_V2 = [
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
    "QA_FAILED",
    "FAILED",
    "CANCELLED",
];
export const FailureClassSchema = z.enum([
    "BUSINESS_CONTENT_INVALID",
    "PROVENANCE_UNRESOLVED",
    "CONTRADICTION",
    "GENERATION_CONFIGURATION_UNAVAILABLE",
    "UNSUPPORTED_GENERATION_INPUT",
    "GENERATION_OUTPUT_REJECTED",
    "QUALITY_GATE_BUSINESS_TRUTH_REJECTED",
    "QUALITY_GATE_GENERATION_OUTPUT_REJECTED",
    "INFRASTRUCTURE_RETRYABLE",
]);
/**
 * Derived Settings readiness (recorded configuration ≠ provider verification).
 * With PROVIDER_ACCESS: NONE, PROVIDER_VERIFIED is normally unreachable.
 */
export const SettingsReadinessStateSchema = z.enum([
    "CONFIGURATION_INCOMPLETE",
    "CONFIGURATION_RECORDED_WITH_DEFERRALS",
    "CONFIGURATION_RECORDED_PROVIDER_UNVERIFIED",
    "PROVIDER_VERIFIED",
]);
export function isGenerationLockActiveStatus(status) {
    return GENERATION_LOCK_ACTIVE_STATUSES.includes(status);
}
export function canSupersedeRunOnNewCreate(status) {
    return RUN_STATUSES_SUPERSEDED_ON_NEW_RUN_CREATE_V2.includes(status);
}
/**
 * Legacy same-row FAILED → RETRIED → PENDING is allowed only for historical
 * runContractVersion < 2. Step 5 runs use new-run infrastructure retry instead.
 */
export function isLegacyRetriedTransitionAllowed(runContractVersion) {
    return Number.isFinite(runContractVersion) && runContractVersion < 2;
}
/** Runtime guard helper — re-export schema for status parsing at boundaries. */
export const Step5RunStatusSchema = RunStatusSchema;
//# sourceMappingURL=step5-generation.js.map